import * as THREE from 'three';
import { TagMarks, standIn, visitorMark, type MarkTarget } from '../tagMarks';
import { seenBy } from '../pick';
import type { Visible } from '../fishSchools';
import { HABITAT_NAMES, HabitatVisitors, visitorsNote, speciesVisitsNote, visitorAtOf, type SpeciesVisitNote, type Visitor, type VisitorAt } from './visitors';
import { hash3 } from '../fishSwim';
import { ExhibitWhales, WHALE_DRAW } from '../whales';
import type { HourSun } from '../sunlight';
import type { WaterSceneData } from './data';
import { HabitatWater } from './water';
import { habitatFov, habitatLights, lightAt, marchComposition, type Composition, type HabitatPick, type HabitatScene } from './habitatScene';
import type { HabitatCameraProfile } from './host';

/**
 * The open water as a scene of its own (M110, the user's third brief, section 6: "an expansive open-ocean environment
 * ... large volumes of unobstructed blue water, very little or no visible seabed ... strong atmospheric perspective";
 * "a sparse but scientifically accurate ocean scene is preferable to one populated with fictitious animals"): the
 * water all round the camera and nothing else solid in it; the humpbacks the simulation has in Hawaii's waters,
 * drawn at their true length, in season and not otherwise; the sun's light falling through it in shafts; its fog and
 * color by the sanctuary's clarity and chlorophyll, its motes by its zooplankton. The twilight's is the same scene in
 * the dark (section 7's framework: "open mesopelagic water, stronger attenuation"), its animals none until the
 * simulation holds any.
 *
 * Each a rendering choice (JUDGED unless said):
 *
 * - `cameraM`: the camera's depth, the blue water's near the surface where its whales swim (`WHALE_DRAW.depthM`, 8 to
 *   20 m), the twilight's in its middle.
 * - `whaleM`: a humpback 12.37 m long, the mean NOAA's drones measured off Maui Nui of its juveniles and adults (van
 *   Aswegen 2025, Table 4.1, as `scripts/whaleSprites.ts` gives it), its calf 5.35 m (`WHALE_DRAW.calfShare`); their
 *   lanes running across the home view, `laneM` either side of its middle, from `lanesZ[0]` to `lanesZ[1]` in front
 *   of it (22 to 44 m from its camera: nearer, a whale is faded as it fills the screen, `NEAR_FADE`), each its own by
 *   its draw (`whaleLane`), so they pass across the view, not toward it, over water deeper than the scene's floor is
 *   drawn (`floorM`, within the 183 m contour).
 * - `shafts`: the sun's light falling through the shallows in `count` shafts `widthM` across, out to `farM`, as bright
 *   as `opacity` under a full sun overhead, fading with depth as the light does; none at night or in the twilight.
 * - `boundsM`: how far the camera may go from the middle; `fov` its lens, upright and on its side.
 */
export const PELAGIC_SCENE = {
  cameraM: { blue: 12, twilight: 450 },
  whaleM: 12.37,
  laneM: 40,
  lanesZ: [8, 30],
  floorM: 180,
  shafts: { count: 12, widthM: [2, 7], farM: 50, opacity: 0.35 },
  boundsM: 70,
  fov: { landscape: 48, portrait: 66 },
} as const;

export class PelagicScene implements HabitatScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 600);
  readonly profile: HabitatCameraProfile;
  private readonly water: HabitatWater;
  private readonly key: THREE.DirectionalLight;
  private readonly whales: ExhibitWhales;
  /** The honu and the seals the day brings (M145). */
  private readonly visitors: HabitatVisitors;
  /** The tagged humpbacks marked (M156). */
  readonly marks = new TagMarks();
  /** The part of the screen the panels leave, as last fitted, and the humpback the tag's mark last stood over (`standIn`, M161). */
  private visible: Visible | undefined;
  private marked: number | null = null;
  private readonly shafts: THREE.Mesh[] = [];
  private readonly shaftMaterial: THREE.ShaderMaterial;
  private readonly depthM: number;
  private data: WaterSceneData | null = null;
  private sunTilt = new THREE.Vector3(0, 1, 0);

  constructor(readonly kind: string, readonly locationName: string, readonly depthRangeM: readonly [number, number], readonly style: 'blue' | 'twilight') {
    const s = PELAGIC_SCENE;
    this.depthM = Math.min(depthRangeM[1] - 5, Math.max(depthRangeM[0] + 5, s.cameraM[style]));
    const bottom = depthRangeM[1] + 60;
    const box = new THREE.Box3(new THREE.Vector3(-800, -bottom, -800), new THREE.Vector3(800, 0, 800));
    const seed = Math.floor(hash3(kind.length, kind.charCodeAt(0) || 0, 141) * 2 ** 31);
    this.water = new HabitatWater(box, style === 'blue', seed);
    const { key, fill } = habitatLights();
    this.key = key;
    // The whales in water deeper than their contour's floor is drawn, so nothing stands under them in view.
    const L = s.whaleM;
    this.whales = new ExhibitWhales({
      x0: -s.laneM, x1: s.laneM, z0: s.lanesZ[0], z1: s.lanesZ[1],
      floorY: () => -s.floorM,
      depthY: (m) => -m,
      depthOf: (y) => Math.max(0, -y),
    }, L, true);
    this.water.fog(this.whales.fogUniforms());
    // The day's visitors across the home view's water, nearer than the whales' (M145).
    this.visitors = new HabitatVisitors({ middle: new THREE.Vector3(0, -this.depthM + 1, 8), eye: new THREE.Vector3(0, -this.depthM, -14), floorAt: null });
    this.water.fog(this.visitors.fogUniforms());
    this.scene.add(this.visitors.group, this.marks.group);
    this.scene.add(this.water.group, this.whales.group, key, key.target, fill);
    // The shafts: soft upright quads, brightest at the top, faded below and at their edges, laid on as light.
    this.shaftMaterial = new THREE.ShaderMaterial({
      uniforms: { uStrength: { value: 0 }, uColor: { value: new THREE.Color(0xdff6ff) } },
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: 'uniform float uStrength; uniform vec3 uColor; varying vec2 vUv; void main() { float edge = 1.0 - abs(vUv.x * 2.0 - 1.0); float a = uStrength * pow(vUv.y, 1.6) * edge * edge; gl_FragColor = vec4(uColor * a, a); }',
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    if (style === 'blue') {
      for (let k = 0; k < s.shafts.count; k++) {
        const width = s.shafts.widthM[0] + (s.shafts.widthM[1] - s.shafts.widthM[0]) * hash3(seed, k, 142);
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, this.depthM + 40), this.shaftMaterial);
        mesh.userData.at = [(hash3(seed, k, 143) - 0.5) * 2 * s.shafts.farM, (hash3(seed, k, 144) - 0.5) * 2 * s.shafts.farM];
        mesh.renderOrder = 7;
        mesh.frustumCulled = false;
        mesh.raycast = () => undefined;
        this.shafts.push(mesh);
        this.scene.add(mesh);
      }
    }
    // Home: looking out across the water the whales cross, a little up toward the light.
    const y = -this.depthM;
    this.profile = {
      home: { position: [0, y, -14], target: [0, y + 1, 12] },
      fov: habitatFov(s.fov.landscape, s.fov.portrait),
      near: 0.2, far: 600,
      distance: [3, 60],
      polar: [0.3 * Math.PI, 0.7 * Math.PI],
      bounds: { min: [-s.boundsM, -Math.min(depthRangeM[1] - 2, this.depthM + 40), -s.boundsM], max: [s.boundsM, -Math.max(1.5, depthRangeM[0] + 1.5), s.boundsM] },
      clearance: 0,
    };
  }

  /** Nothing solid stands in the open water: the camera's only bounds are its box. */
  groundAt(): number {
    return -Infinity;
  }

  /** Another world laid on the scene (a game loaded, a new one), its visitors' looks taken afresh (review 31). */
  newWorld(): void {
    this.visitors.newWorld();
  }

  setData(data: WaterSceneData): void {
    const e = data.environment;
    this.water.setState(e.kdPar.value, e.chlorophyllMgM3.value, e.zooMmolNPerM3.value);
    const first = this.data === null;
    this.data = data;
    this.whales.set(data.whales?.sprites ?? { whales: 0, calves: 0 }, first);
    this.visitors.set(data.visitors);
    // Opened in the season with the first whale crossing the home view.
    if (first && (data.whales?.sprites.whales ?? 0) > 0) this.whales.meet(0);
  }

  setLight(sun: HourSun): void {
    this.water.setLight(sun, -this.camera.position.y);
    const tint = lightAt(sun, this.key, this.water.seen);
    this.whales.setLight(...tint);
    this.visitors.setLight(...tint);
    // The shafts by the light down here from the sun now, none when it is down, and the way it falls, bent at the
    // surface (Snell's law, seawater's index about 1.33).
    const up = sun.beam > 0 && sun.elevationDeg > 0;
    (this.shaftMaterial.uniforms.uStrength as THREE.IUniform<number>).value = up ? PELAGIC_SCENE.shafts.opacity * Math.min(1, sun.level) * Math.min(1, this.water.seen) : 0;
    const zenith = ((90 - Math.max(0, sun.elevationDeg)) * Math.PI) / 180;
    const bent = Math.asin(Math.sin(zenith) / 1.33);
    const across = new THREE.Vector3(sun.direction[0], 0, sun.direction[2]);
    if (across.lengthSq() > 1e-9) across.normalize();
    this.sunTilt.copy(across.multiplyScalar(Math.sin(bent))).setY(Math.cos(bent));
  }

  animate(hour: number, _dayLengthHours: number, seconds: number, calm: boolean): void {
    this.whales.swim(seconds, calm);
    this.visitors.setHour(hour);
    this.visitors.swim(seconds, calm);
    this.water.follow(this.camera, this.camera.far * 0.8, seconds, calm);
    // Each shaft about the camera, its top at the surface, leaning toward the sun, turned to face the camera about its own line.
    const c = this.camera.position;
    const up = this.sunTilt;
    for (const mesh of this.shafts) {
      const [ox, oz] = mesh.userData.at as [number, number];
      const length = (mesh.geometry as THREE.PlaneGeometry).parameters.height;
      const top = new THREE.Vector3(c.x + ox, 0, c.z + oz);
      const middle = top.clone().addScaledVector(up, -length / 2);
      mesh.position.copy(middle);
      const toCamera = c.clone().sub(middle);
      const side = new THREE.Vector3().crossVectors(up, toCamera).normalize();
      const facing = new THREE.Vector3().crossVectors(side, up).normalize();
      mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(side, up, facing));
    }
  }

  fit(_canvasPx?: number, _seconds?: number, visible?: Visible): void {
    this.visible = visible;
    // No level of detail: a few whales, each drawn whole, turned the way the camera sees them move (M142: its home view
    // looks along the world's +z, its right the world's -x, and by the world's x they had swum backward).
    this.whales.face(this.camera);
    this.visitors.face(this.camera);
  }

  pick(x: number, y: number, widthPx: number, heightPx: number, withinPx: number): HabitatPick | null {
    // A visitor first: its lane is nearer than the whales'.
    const visitor = this.visitors.pick(this.camera, x, y, widthPx, heightPx, withinPx);
    if (visitor) return { visitor };
    const whale = this.whales.pick(this.camera, x, y, widthPx, heightPx, withinPx);
    return whale >= 0 ? { whale } : null;
  }

  handOverVisitor(from: string, to: string, tag?: Visitor['tag']): void {
    this.visitors.handOver(from, to, tag);
  }

  visitorAt(id: string): VisitorAt | null {
    const d = this.visitors.drawnOf(id);
    return d ? visitorAtOf(d, this.data?.visitOdds, this.data?.visits) : null;
  }

  visitOf(kind: Visitor['kind']): SpeciesVisitNote | null {
    return this.data?.visits.find((v) => v.species === kind) ?? null;
  }

  visitorGone(id: string): Visitor | null {
    return this.visitors.drawnOf(id) ? null : this.visitors.goneOf(id);
  }

  /** A whale as the card tells it: its middle, its real depth, its calf beside it, and the sprites drawn. */
  whaleAt(index: number): { middle: THREE.Vector3; depthM: number; calf: boolean; sprites: { whales: number; calves: number } } | null {
    const w = this.whales.at(index);
    if (!w) return null;
    return { middle: w.whale.middle, depthM: -w.whale.middle.y, calf: w.calf !== null, sprites: this.whales.counts() };
  }

  /** The whales drawn now, for the tests. */
  whalesDrawn(): ReturnType<ExhibitWhales['drawn']> {
    return this.whales.drawn();
  }

  /** The water's state, for the tests. */
  waterState(): ReturnType<HabitatWater['state']> {
    return this.water.state();
  }

  /** The shafts' strength now, for the tests. */
  shaftStrength(): number {
    return this.shaftMaterial.uniforms.uStrength?.value as number;
  }

  composition(cols = 30, rows = 50): Composition {
    return marchComposition(this.camera, () => -PELAGIC_SCENE.floorM, () => false, 0, 1 / Math.max(1e-6, this.water.state().fogPerM), 0.25, cols, rows);
  }

  /** The Drawn row: what it shows and what it does not. */
  note(): string {
    const d = this.data;
    const whales = d?.whales;
    // A site visited: its plankton its ocean region's, as the simulation holds them (`data.ts`).
    const plankton = d?.environment.chlorophyllMgM3.scope === 'region' ? 'its region\'s chlorophyll and plankton' : 'the sanctuary\'s chlorophyll and plankton';
    const parts = [this.style === 'blue'
      ? `the sanctuary's ${this.locationName}, its water the sanctuary's clarity and ${plankton}, its light the hour's`
      : `the sanctuary's ${this.locationName}, dark as the light that reaches it, its water the sanctuary's clarity and ${plankton}`];
    if (whales) parts.push(`a humpback for each ${whales.perSprite.toLocaleString('en-US')} of the ${Math.round(whales.inHawaii.value).toLocaleString('en-US')} in Hawaiʻi's waters today, at their true length, ${whales.sprites.whales} now (Maui Nui's share of them no source read gives)`);
    if (d?.visitOdds) parts.push(visitorsNote(d.visitors, d.visitOdds));
    if (d?.visits.length) parts.push(speciesVisitsNote(d.visits, HABITAT_NAMES));
    for (const n of d?.notDrawn ?? []) parts.push(`not drawn: ${n}`);
    return parts.join('; ');
  }

  /** Where a tag's mark stands (M156): over one of the humpbacks the camera sees, kept while it is seen (`standIn`, M161); null for the rest. */
  markAt(target: MarkTarget): THREE.Vector3 | null {
    if ('island' in target) return null;
    // A tagged visitor drawn here over itself (M203).
    if (target.species !== 'humpback') return visitorMark(target, this.visitors);
    const w = standIn(this.whales.drawn().filter((d) => !d.calf), (d) => d.index, (d) => seenBy(d, this.camera, this.visible), this.marked ?? undefined);
    this.marked = w?.index ?? null;
    return w?.middle ?? null;
  }

  dispose(): void {
    this.marks.dispose();
    this.whales.dispose();
    this.visitors.dispose();
    this.water.dispose();
    for (const mesh of this.shafts) mesh.geometry.dispose();
    this.shaftMaterial.dispose();
  }
}

export { WHALE_DRAW };
